/* File: src\contexts\SettingsContext.js */

/* src/contexts/SettingsContext.js */
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import {
    getFirestore,
    doc,
    getDoc,
    setDoc,
    serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig'; // Import db and auth
import { onAuthStateChanged } from 'firebase/auth'; // Need listener to know the user
// Import useProfile hook to get the activeProfileId
import { useProfile } from './ProfileContext';
// Import audioManager - BUT DON'T USE IT IN getDefaultSettings DIRECTLY
import audioManager from '../utils/audioManager';

/**
 * Defines the default structure and values for profile-specific settings.
 * Exported so other components can access defaults for initialization.
 * Selected Voice ID is set to null initially, handled by the provider.
 */
export const getDefaultSettings = () => ({
    // Gameplay settings
    keyBindings: {
        visual: 'ArrowLeft', // Default visual match key
        audio: 'ArrowRight', // Default audio match key
    },
    instantFeedback: true, // Default gameplay feedback setting (true = enabled)
    // Audio settings
    selectedVoiceId: null, // Set to null initially. Will be validated/set by useEffect in the Provider.
    // Time zone setting
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Default to browser's timezone
    // --- Future settings examples ---
    // sound: true,
    // animations: true,
    // darkMode: false,
    // journalPromptFrequency: 'daily',
});

// Create the React Context object
const SettingsContext = createContext({
    settings: getDefaultSettings(), // Initialize with basic defaults
    updateSetting: () => {}, // Placeholder for update function
    isLoading: true, // Start in loading state
    error: null,
    availableVoices: {}, // Start with empty object, populated by provider
});

/**
 * Custom hook to easily consume the SettingsContext in components.
 * Ensures the hook is used within a SettingsProvider.
 */
export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

/**
 * SettingsProvider Component
 * Manages loading, saving, and providing profile-specific application settings.
 * Uses localStorage as a cache/offline storage and syncs with Firestore when possible.
 */
export const SettingsProvider = ({ children }) => {
    // Initialize state with basic defaults first
    const [settings, setSettings] = useState(getDefaultSettings());
    const [isLoading, setIsLoading] = useState(true); // Track loading state
    const [error, setError] = useState(null); // Track potential errors
    const [currentUserId, setCurrentUserId] = useState(null);

    // Get activeProfileId from the ProfileContext for Firestore path
    const { activeProfileId } = useProfile();

    // --- Get Available Voices from audioManager (Memoized) ---
    const availableVoices = useMemo(() => audioManager.getAvailableVoices(), []);
    const availableVoiceIds = useMemo(() => Object.keys(availableVoices), [availableVoices]);
    // const firstAvailableVoiceId = useMemo(() => (availableVoiceIds.length > 0 ? availableVoiceIds[0] : null), [availableVoiceIds]); // Keep for reference if needed

    // --- MODIFICATION START: Define preferred default voice ---
    const preferredDefaultVoiceId = useMemo(() => {
        const ELEANOR_VOICE_ID = "google-en-gb-wavenet-n"; // ID for "Eleanor (UK Female)"
        if (availableVoices && availableVoices[ELEANOR_VOICE_ID]) {
            return ELEANOR_VOICE_ID; // Use Eleanor if available
        }
        // Fallback to the first available if Eleanor isn't present
        return availableVoiceIds.length > 0 ? availableVoiceIds[0] : null;
    }, [availableVoices, availableVoiceIds]); // Depend on the list of voices
    // --- MODIFICATION END ---


    // Effect to get current user ID and handle logout reset
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            const newUserId = user ? user.uid : null;
            if (newUserId !== currentUserId) {
                setCurrentUserId(newUserId);
                // If user logs out, reset settings state to default and clear local storage
                if (!newUserId) {
                    console.log("[SettingsContext] User logged out. Resetting settings to defaults.");
                    const defaults = getDefaultSettings();
                    // --- MODIFICATION START: Use preferred default on logout ---
                    defaults.selectedVoiceId = preferredDefaultVoiceId; // Set the calculated preferred default
                    // --- MODIFICATION END ---
                    setSettings(defaults);
                    localStorage.removeItem('userAppSettings'); // Clear storage on logout
                    setIsLoading(false); // Stop loading if user logs out during initial load
                    setError(null);
                }
            }
        });
        return () => unsubscribe();
    // --- MODIFICATION START: Add preferredDefaultVoiceId dependency ---
    }, [currentUserId, preferredDefaultVoiceId]); // Depend on preferredDefaultVoiceId for logout reset
    // --- MODIFICATION END ---


    // Effect to Load Settings (from localStorage first, then Firestore if applicable)
    useEffect(() => {
        // Don't run load logic if user just logged out (handled by auth listener)
        if (currentUserId === null && !isLoading) return;

        setIsLoading(true);
        setError(null);
        // Start with base defaults and set the initial default voice ID
        let loadedSettingsState = {
            ...getDefaultSettings(),
            // --- MODIFICATION START: Use preferred default for initial state ---
            selectedVoiceId: preferredDefaultVoiceId // Set initial default based on preference
            // --- MODIFICATION END ---
        };

        // 1. Try loading from localStorage
        try {
            const storedSettings = localStorage.getItem('userAppSettings');
            if (storedSettings) {
                const parsed = JSON.parse(storedSettings);
                // Merge stored with defaults + preferred default
                loadedSettingsState = {
                    ...loadedSettingsState, // Start with defaults including preferred default voice
                    ...parsed, // Overwrite with parsed values
                    // Explicitly merge nested objects to avoid overwriting defaults partially
                    keyBindings: (parsed.keyBindings && typeof parsed.keyBindings === 'object')
                                 ? { ...loadedSettingsState.keyBindings, ...parsed.keyBindings }
                                 : loadedSettingsState.keyBindings,
                    // Validate boolean specifically
                    instantFeedback: typeof parsed.instantFeedback === 'boolean'
                                     ? parsed.instantFeedback
                                     : loadedSettingsState.instantFeedback,
                };

                // Validate the loaded selectedVoiceId against CURRENTLY available voices
                if (!parsed.selectedVoiceId || !availableVoices[parsed.selectedVoiceId]) {
                    // --- MODIFICATION START: Use preferred default on invalid localStorage ---
                    console.warn(`[SettingsContext Load] localStorage voice ID '${parsed.selectedVoiceId}' invalid or unavailable. Reverting to preferred default: '${preferredDefaultVoiceId}'.`);
                    loadedSettingsState.selectedVoiceId = preferredDefaultVoiceId; // Use preferred default if invalid
                    // --- MODIFICATION END ---
                } else {
                    loadedSettingsState.selectedVoiceId = parsed.selectedVoiceId; // Use valid stored value
                }

                // Try to detect timezone if not already set (or invalid)
                if (!parsed.timezone) {
                    try {
                        // Get timezone from browser API
                        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        loadedSettingsState.timezone = browserTimezone;
                        console.log(`[SettingsContext Load] Detected browser timezone: ${browserTimezone}`);
                    } catch (e) {
                        console.warn("Error detecting browser timezone:", e);
                        loadedSettingsState.timezone = "UTC"; // Default to UTC if detection fails
                    }
                } else {
                    // If timezone is set, use it (assuming it's valid)
                    loadedSettingsState.timezone = parsed.timezone;
                }

                console.log("[SettingsContext Load] Loaded settings from localStorage:", loadedSettingsState);
            } else {
                 console.log("[SettingsContext Load] No settings found in localStorage. Using defaults.");
                 // State already initialized with defaults including preferred default voice
                 // Ensure timezone is set based on browser if not in localStorage
                 try {
                     const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                     loadedSettingsState.timezone = browserTimezone;
                     console.log(`[SettingsContext Load] Detected browser timezone: ${browserTimezone}`);
                 } catch (e) {
                     console.warn("Error detecting browser timezone:", e);
                     loadedSettingsState.timezone = "UTC"; // Default to UTC if detection fails
                 }
            }
        } catch (e) {
            console.error("Error loading/parsing settings from localStorage:", e);
            setError("Failed to load preferences.");
            // State uses defaults including preferred default voice
            // Ensure timezone is set based on browser if localStorage fails
            try {
                const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                loadedSettingsState.timezone = browserTimezone;
                console.log(`[SettingsContext Load] Detected browser timezone after localStorage error: ${browserTimezone}`);
            } catch (e) {
                console.warn("Error detecting browser timezone:", e);
                loadedSettingsState.timezone = "UTC"; // Default to UTC if detection fails
            }
        }

        // Update state with result from localStorage/defaults BEFORE checking Firestore
        setSettings(loadedSettingsState);

        // 2. If logged in and profile selected, try fetching from Firestore to potentially update
        if (currentUserId && activeProfileId) {
            console.log(`[SettingsContext Load] User/Profile active (${currentUserId}/${activeProfileId}). Checking Firestore...`);
            const settingsDocRef = doc(db, 'users', currentUserId, 'profiles', activeProfileId, 'profileSettings', 'settings');

            getDoc(settingsDocRef).then(docSnap => {
                let firestoreSettingsToApply = null; // Hold settings to apply after promise
                if (docSnap.exists()) {
                    const loadedData = docSnap.data();
                    // Start with the current state (derived from local/defaults) and merge Firestore data on top
                    const mergedFirestore = {
                        ...settings, // Use current state as base
                        ...loadedData, // Overwrite with Firestore data
                        // Explicitly merge/validate nested objects/specific types
                        keyBindings: (loadedData.keyBindings && typeof loadedData.keyBindings === 'object')
                                     ? { ...settings.keyBindings, ...loadedData.keyBindings }
                                     : settings.keyBindings,
                        instantFeedback: typeof loadedData.instantFeedback === 'boolean'
                                         ? loadedData.instantFeedback
                                         : settings.instantFeedback,
                    };

                     // Validate Firestore selectedVoiceId against CURRENT available voices
                     if (!loadedData.selectedVoiceId || !availableVoices[loadedData.selectedVoiceId]) {
                         // --- MODIFICATION START: Use preferred default on invalid Firestore data ---
                         console.warn(`[SettingsContext Load] Firestore voice ID '${loadedData.selectedVoiceId}' invalid or unavailable. Using preferred default: '${preferredDefaultVoiceId}'.`);
                         mergedFirestore.selectedVoiceId = preferredDefaultVoiceId; // Use preferred default if invalid
                         // --- MODIFICATION END ---
                     } else {
                          mergedFirestore.selectedVoiceId = loadedData.selectedVoiceId; // Use valid Firestore value
                     }

                     // Validate timezone from Firestore or fall back to current state
                     if (!loadedData.timezone) {
                         console.warn(`[SettingsContext Load] Firestore timezone is missing. Keeping current state value: '${settings.timezone}'.`);
                         mergedFirestore.timezone = settings.timezone; // Keep current state value
                     } else {
                         mergedFirestore.timezone = loadedData.timezone; // Use Firestore value
                     }

                    console.log("[SettingsContext Load] Loaded settings from Firestore:", mergedFirestore);

                    // Only mark for update if Firestore data is meaningfully different from current state
                    if (JSON.stringify(mergedFirestore) !== JSON.stringify(settings)) {
                         console.log("[SettingsContext Load] Firestore settings differ. Marking for state update.");
                         firestoreSettingsToApply = mergedFirestore; // Mark for application outside promise chain
                    } else {
                         console.log("[SettingsContext Load] Firestore settings match current state. No update needed.");
                    }
                } else {
                    // No Firestore doc? Save the current state (local/defaults) to Firestore
                    console.log("[SettingsContext Load] No settings doc in Firestore. Saving current state...");
                    saveSettingsToFirestore(settings); // Save the current state variable
                }

                // Apply Firestore changes if needed AFTER the main getDoc logic finishes
                if (firestoreSettingsToApply) {
                    setSettings(firestoreSettingsToApply);
                     try {
                        // Update localStorage with the fresher Firestore data
                        localStorage.setItem('userAppSettings', JSON.stringify(firestoreSettingsToApply));
                    } catch (e) {
                        console.error("Error updating localStorage with Firestore settings:", e);
                    }
                }

            }).catch(err => {
                console.error(`Error loading settings from Firestore for profile ${activeProfileId}:`, err);
                setError("Failed to check server for updated settings.");
                // Don't overwrite local settings if Firestore fails
            }).finally(() => {
                setIsLoading(false); // Mark loading as complete after Firestore check
            });
        } else {
            // Not logged in or no active profile, loading is finished (localStorage/defaults used)
            setIsLoading(false);
        }
    // --- MODIFICATION START: Add preferredDefaultVoiceId dependency ---
    // Re-run when user, profile, available voices, or the preferred default changes
    }, [currentUserId, activeProfileId, availableVoices, preferredDefaultVoiceId]); // Add voice dependencies
    // --- MODIFICATION END ---


    // Function to save settings to Firestore (only if logged in and profile active)
    const saveSettingsToFirestore = useCallback(async (settingsToSave) => {
        if (!currentUserId || !activeProfileId) {
            // console.warn("[SettingsContext Save] Cannot save to Firestore: No user or active profile.");
            return false; // Cannot save without user/profile
        }

        const settingsDocRef = doc(db, 'users', currentUserId, 'profiles', activeProfileId, 'profileSettings', 'settings');
        const baseDefaults = getDefaultSettings(); // For structure reference
        let validVoiceId = null; // Start with null

        // --- MODIFICATION START: Validate against preferred default ---
        // Validate the voice ID against the *currently* available ones, using preferred default as fallback
        if (availableVoiceIds.length > 0) {
            validVoiceId = (settingsToSave.selectedVoiceId && availableVoices[settingsToSave.selectedVoiceId])
                           ? settingsToSave.selectedVoiceId
                           : preferredDefaultVoiceId; // Default to preferred if invalid/null
        }
         // else validVoiceId remains null if no voices available
        // --- MODIFICATION END ---

        try {
            // Prepare payload, ensuring structure and adding timestamp
            // Start with validated values from settingsToSave or fall back to defaults/validated voice
            const payload = {
                 keyBindings: (settingsToSave.keyBindings && typeof settingsToSave.keyBindings.visual === 'string' && typeof settingsToSave.keyBindings.audio === 'string')
                               ? settingsToSave.keyBindings
                               : baseDefaults.keyBindings,
                 instantFeedback: typeof settingsToSave.instantFeedback === 'boolean'
                                  ? settingsToSave.instantFeedback
                                  : baseDefaults.instantFeedback,
                 selectedVoiceId: validVoiceId, // Save the validated/defaulted voice ID
                 timezone: settingsToSave.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone, // Use provided timezone or detect from browser
                 updatedAt: serverTimestamp()
            };

            // Optionally include other keys from settingsToSave if they exist and are not handled above
            // Example:
            // if (settingsToSave.hasOwnProperty('darkMode')) {
            //     payload.darkMode = settingsToSave.darkMode;
            // }

            await setDoc(settingsDocRef, payload, { merge: true }); // Use merge: true to avoid overwriting other potential fields
            console.log(`[SettingsContext Save] Settings saved to Firestore for profile ${activeProfileId}. Voice: ${validVoiceId}, Timezone: ${payload.timezone}`);
            return true;
        } catch (err) {
            console.error(`Error saving settings to Firestore for profile ${activeProfileId}:`, err);
            setError("Failed to save settings to your account.");
            return false;
        }
    // --- MODIFICATION START: Add voice/preferred default dependencies ---
    }, [currentUserId, activeProfileId, availableVoices, availableVoiceIds, preferredDefaultVoiceId]); // Add voice dependencies
    // --- MODIFICATION END ---


    // Action function exposed to components for updating a single setting
    // Wrapped in useCallback for stability
    const updateSetting = useCallback((key, value) => {
        // Use functional update to ensure we're working with the latest state
        setSettings(prevSettings => {
            const newSettings = { ...prevSettings, [key]: value };

            // Update localStorage immediately
            try {
                localStorage.setItem('userAppSettings', JSON.stringify(newSettings));
                 // console.log(`SettingsContext: Updated '${key}' locally and in localStorage.`); // Reduce noise
            } catch (e) {
                 console.error("Error saving settings to localStorage:", e);
                 setError("Failed to save preference locally.");
                 // Consider if you should return prevSettings here to revert optimistic update
            }

            // Attempt to save to Firestore in the background (debouncing could be added)
            saveSettingsToFirestore(newSettings); // Fire-and-forget Firestore save

            return newSettings; // Return the new state for React to use
        });

    }, [saveSettingsToFirestore]); // Dependency: only the save function


    // Assemble the final context value object using useMemo
    const value = useMemo(() => ({
        settings,
        updateSetting, // Provide the function to update a single setting
        isLoading, // Expose loading state
        error, // Expose error state
        availableVoices: availableVoices, // Expose the memoized available voices object
        // Removed resetSettings as updateSetting handles individual changes
        // Removed updateSettings (plural) as updateSetting (singular) is preferred
    }), [settings, updateSetting, isLoading, error, availableVoices]); // Dependencies for memoization

    // Render the provider, passing the context value to its children
    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};